import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orgApi, studentApi, batchApi, quizApi, examLibraryApi, apiErrorMessage } from '../services/api';
import { useOrg } from '../context/OrgContext';
import { Button, Card, Metric, Bar, PageSkeleton, ErrorState, EmptyState } from '../components/ui';
import SetupChecklist from '../components/SetupChecklist';
import JoinCodeCard from '../components/JoinCodeCard';
import { computeSetupProgress, shouldShowSetup } from '../utils/setupChecklist';
import { resolveQuizState, quizStateLabel } from '../utils/quizState';

export default function DashboardPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      orgApi.getDashboard(org.id),
      studentApi.list(org.id),
      batchApi.list(org.id),
      quizApi.list(org.id),
      examLibraryApi.enrolled(org.id),
    ])
      .then(([dashRes, studentsRes, batchesRes, quizzesRes, examsRes]) => {
        setDashboard(dashRes.data);
        setStudents(studentsRes.data || []);
        setBatches(batchesRes.data || []);
        setQuizzes(quizzesRes.data || []);
        setExams(examsRes.data || []);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load dashboard')))
      .finally(() => setLoading(false));
  }, [org.id]);

  if (loading) return <PageSkeleton />;
  if (error) return <ErrorState message={error} />;

  const progress = computeSetupProgress({ exams, batches, students, quizzes });
  const settingUp = shouldShowSetup(progress);

  const studentsById = Object.fromEntries(students.map((s) => [s.userId, s]));
  const atRisk = (dashboard.atRiskStudents || []).map((r) => ({
    ...r,
    name: studentsById[r.userId]?.userName || r.userId,
    batchName: studentsById[r.userId]?.batchName || '—'
  }));
  const pendingRequests = students.filter((s) => s.status !== 'active' && !s.invitedEmail).length;
  const recentTests = [...quizzes]
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 5);
  const completion = Math.round((dashboard.completionRate || 0) * 100);

  return (
    <>
      {pendingRequests > 0 && (
        <div className="notice-row">
          <span>{pendingRequests} student{pendingRequests === 1 ? '' : 's'} joined with your code and {pendingRequests === 1 ? 'is' : 'are'} waiting for approval.</span>
          <Button variant="secondary" onClick={() => navigate('/students')}>Review requests</Button>
        </div>
      )}

      {settingUp ? (
        <div className="grid two">
          <SetupChecklist progress={progress} />
          <JoinCodeCard code={org.code} />
        </div>
      ) : (
        <div className="metrics">
          <Metric label="Active students" value={dashboard.activeStudentCount} hint={`${students.length} in total`} />
          <Metric label="Test completion" value={`${completion}%`} hint={`Across ${dashboard.launchedQuizCount ?? 0} launched test${dashboard.launchedQuizCount === 1 ? '' : 's'}`} />
          <Metric label="Average score" value={`${dashboard.averageScore ?? 0}%`} hint={`From ${dashboard.totalAttempts} attempts`} />
          <Metric label="Needs attention" value={atRisk.length} hint="No attempts or below 40%" />
        </div>
      )}

      <div className="grid two">
        <Card>
          <div className="section-title">
            <div><h2>Batches</h2><p>Students per batch</p></div>
            <Button variant="text" onClick={() => navigate('/batches')}>View batches</Button>
          </div>
          <div className="bars">
            {batches.length
              ? batches.map((b) => {
                  const max = Math.max(1, ...batches.map((x) => x.studentCount));
                  return <Bar key={b.id} label={b.name} value={Math.round((b.studentCount / max) * 100)} />;
                })
              : (
                <EmptyState title="No batches yet" action={<Button variant="secondary" onClick={() => navigate('/batches')}>Create a batch</Button>}>
                  Batches let you group students and control who receives each test.
                </EmptyState>
              )}
          </div>
        </Card>
        <Card>
          <div className="section-title">
            <div><h2>Recent tests</h2><p>Open a test to see who took it</p></div>
            <Button variant="text" onClick={() => navigate('/manage-quiz')}>All tests</Button>
          </div>
          {recentTests.length ? (
            <table>
              <thead><tr><th>Test</th><th>State</th><th></th></tr></thead>
              <tbody>
                {recentTests.map((q) => {
                  const state = resolveQuizState(q);
                  return (
                    <tr key={q.id} className="clickable" onClick={() => navigate(`/manage-quiz/${q.id}`)}>
                      <td><b>{q.name}</b><small>{q.questionCount} questions</small></td>
                      <td><span className={state === 'open' ? 'status' : state === 'upcoming' ? 'pending' : 'status draft'}>{quizStateLabel(state)}</span></td>
                      <td><Button variant="text">Results</Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <EmptyState title="No tests yet" action={progress.steps.find((s) => s.key === 'quiz')?.blocked ? null : <Button variant="secondary" onClick={() => navigate('/manage-quiz')}>Launch a test</Button>}>
              {progress.steps.find((s) => s.key === 'quiz')?.blocked
                ? 'Finish the setup steps above, then launch your first test.'
                : 'Quick launch picks questions for you; the screener lets you choose topics.'}
            </EmptyState>
          )}
        </Card>
      </div>

      {!settingUp && (
        <Card className="table-card">
          <div className="section-title">
            <div><h2>Student watchlist</h2><p>Students with no attempts, or scoring below 40%</p></div>
            <Button variant="text" onClick={() => navigate('/students')}>All students</Button>
          </div>
          <table>
            <thead><tr><th>Student</th><th>Batch</th><th>Attempts</th><th>Average score</th></tr></thead>
            <tbody>
              {atRisk.length ? atRisk.map((r) => (
                <tr key={r.userId} className="clickable" onClick={() => navigate(`/students/${r.userId}`)}>
                  <td><b>{r.name}</b></td>
                  <td>{r.batchName}</td>
                  <td>{r.attemptCount}</td>
                  <td><b className="danger">{r.averageScore != null ? `${r.averageScore}%` : 'No attempts'}</b></td>
                </tr>
              )) : (
                <tr><td colSpan="4">
                  <EmptyState title="All clear">No students need attention right now.</EmptyState>
                </td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
